import {dateFormatUTC} from '@/lib/utils/date.utils';
import {getMessage} from '@/lib/utils/i18n.utils';
import {Box, Text, TextProps} from '@eds-web/react';
import {useMessages} from '@epic-mw/i18n';
import {css} from '@panda/css';

const lastUpdatedStyle = css({
	display: 'flex',
	justifyContent: 'center',
	marginBottom: '25px !important'
});

const titleStyle = css({
	fontWeight: 350,
	padding: '25px 0 !important'
});

const bodyStyle = css({
	'& h1, & h2, & h3, & h4, & h5': {
		color: 'white',
		margin: '25px 0'
	},

	'& h1': {
		textStyle: 'h1'
	},

	'& h2': {
		textStyle: 'h2'
	},

	'& h3': {
		textStyle: 'h3'
	},

	'& h4': {
		textStyle: 'h4',
		fontWeight: 'normal'
	},

	'& h5': {
		textStyle: 'h5',
		fontSize: 'md !important',
		'& > span': {
			fontSize: 'md !important'
		}
	},

	'& a': {
		color: 'eds.icon.link.primary.default',
		fontWeight: 'normal',
		borderBottom: '1px solid',
		textDecoration: 'none',
		_hover: {
			textDecoration: 'none',
			color: 'eds.icon.link.primary.press'
		}
	},

	'& ol': {
		listStyleType: 'lower-alpha'
	}
});

export interface EulaItemProps {
	className?: string;
	eulaId: string;
	locale: string;
	title?: string;
	titleProps?: Partial<TextProps<any>>;
	body: string;
	bodyProps?: Partial<TextProps<any>>;
	lastModifiedTimestamp?: string;
	lastModifiedTimestampProps?: Partial<TextProps<any>>;
	eulaBottomRef?: React.LegacyRef<HTMLDivElement>;
}

export const EulaItem = ({
	className,
	eulaId,
	locale,
	title,
	titleProps,
	body,
	bodyProps,
	lastModifiedTimestamp,
	lastModifiedTimestampProps,
	eulaBottomRef
}: EulaItemProps) => {
	const messages = useMessages();

	return (
		<Box key={eulaId} className={className}>
			{title && (
				<Text
					className={titleStyle}
					variant="heading2XL"
					color="primary"
					align="center"
					{...titleProps}>
					{title}
				</Text>
			)}

			{lastModifiedTimestamp && (
				<Text
					className={lastUpdatedStyle}
					variant="eyebrowMD"
					color="secondary"
					align="center"
					{...lastModifiedTimestampProps}>
					<em>
						{getMessage(messages, 'epic.app.time.last_updated', [
							dateFormatUTC(lastModifiedTimestamp, locale)
						])}
					</em>
				</Text>
			)}

			<Text
				as="div"
				className={bodyStyle}
				color="secondary"
				variant="paragraphMD"
				{...bodyProps}>
				<div dangerouslySetInnerHTML={{__html: body}} />
				<div ref={eulaBottomRef} style={{minHeight: '1px'}} />
			</Text>
		</Box>
	);
};
